import { useState, type JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Archive, ArchiveRestore, ArrowLeft, Download, Loader2, RefreshCw, RotateCw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { Pagination } from '@/components/ui/pagination';
import {
  DataTable,
  DataTableCell,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableRow,
} from '@/components/ui/data-table';
import { useUrlState } from '@/hooks/useUrlState';
import { useDiscoveryClient } from '../api/context';
import { ApiError } from '../api/types';
import {
  useEmailCampaign,
  useRecipients,
  useRetryEmailCampaign,
  useSyncEmailCampaign,
  useUpdateEmailCampaign,
} from '../hooks/email';
import { PLATFORM_LABELS, profileUrl } from '../lib/platform';
import { useDiscoveryPaths } from '../workspace/paths';
import { DebouncedInput, Field, Note } from '../workspace/ui';
import { FACE_LABELS, KIND_LABELS, STAGE_FILTERS, compact, funnelProblems, recipientStep, sequenceStages } from './model';
import { BreakdownTable, FunnelStrip, SendStatusPill, downloadBlob, when, SequenceStrip } from './parts';
import { EMAIL_KINDS, type Recipient } from './types';

const PAGE_SIZE = 50;

/** One campaign: its funnel, how each segment did, and every recipient. */
export function EmailCampaignScreen(): JSX.Element {
  const { campaignId = '' } = useParams();
  const paths = useDiscoveryPaths();
  const client = useDiscoveryClient();
  const { data, error, isLoading } = useEmailCampaign(campaignId);
  const sync = useSyncEmailCampaign();
  const retry = useRetryEmailCampaign();
  const update = useUpdateEmailCampaign();
  const [url, setUrl] = useUrlState({ stage: '', kind: '', q: '', page: 1 });
  const set = (patch: Partial<typeof url>) => setUrl({ ...patch, page: 1 });
  const [downloading, setDownloading] = useState(false);

  const recipients = useRecipients(campaignId, {
    ...(url.stage ? { stage: url.stage } : {}),
    ...(url.kind ? { kind: url.kind } : {}),
    ...(url.q.trim() ? { search: url.q.trim() } : {}),
    limit: PAGE_SIZE,
    offset: (Math.max(1, url.page) - 1) * PAGE_SIZE,
  });

  const back = (
    <Link to={paths.email('campaigns')} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
      <ArrowLeft className="h-3.5 w-3.5" />
      Email campaigns
    </Link>
  );

  if (error) {
    const missing = error instanceof ApiError && error.status === 404;
    return (
      <div className="space-y-4">
        {back}
        <Note tone="error" role="alert">
          {missing ? "That campaign doesn't exist, or belongs to another team." : `Couldn't load the campaign: ${(error as Error).message}`}
        </Note>
      </div>
    );
  }
  if (isLoading || !data) {
    return (
      <div className="space-y-4">
        {back}
        <p className="text-sm text-muted-foreground">Loading campaign…</p>
      </div>
    );
  }

  const { campaign, funnel, breakdowns, progress } = data;
  const stepCount = campaign.stepCount ?? null;
  // Per-email filters, after the funnel stages, for however long this sequence is.
  const stageOptions = [
    ...STAGE_FILTERS,
    ...(progress ? sequenceStages(progress, stepCount).filter((s) => s.key.startsWith('step_')).map((s) => ({ value: s.key, label: s.label })) : []),
  ];
  const instantly = campaign.provider === 'instantly';
  const problems = funnelProblems(funnel);
  const retryable = funnel.failed + funnel.queued;

  async function download() {
    setDownloading(true);
    try {
      downloadBlob(await client.email.exportCampaignCsv(campaign.publicId), `${campaign.name.replace(/[^\w.-]+/g, '-')}.csv`);
    } catch (e) {
      toast.error(`Couldn't download: ${(e as Error).message}`);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="space-y-6">
      {back}
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="truncate text-[26px] font-bold tracking-[-0.03em]">{campaign.name}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {instantly ? 'Instantly campaign' : 'CSV export'} · started {when(campaign.createdAt)}
            {campaign.createdByName && ` by ${campaign.createdByName}`}
            {instantly && ` · results updated ${when(campaign.lastSyncedAt)}`}
            {campaign.archivedAt && ' · archived'}
          </p>
          {campaign.linkTemplate && (
            <p className="mt-1 truncate text-sm text-muted-foreground" title={campaign.linkTemplate}>
              Link: <span className="font-mono text-xs">{campaign.linkTemplate}</span>
              {campaign.trackClicks ? ' · clicks counted per creator' : ' · sent as written'}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {instantly && (
            <Button
              variant="outline"
              size="sm"
              disabled={sync.isPending}
              onClick={() =>
                sync.mutate(campaign.publicId, {
                  onSuccess: (r) => toast.success('Results updated', { description: `${r.updated.toLocaleString()} recipients changed.` }),
                  onError: (e) => toast.error(`Couldn't update: ${(e as Error).message}`),
                })
              }
            >
              <RefreshCw className={cn('h-3.5 w-3.5', sync.isPending && 'animate-spin')} />
              Update results
            </Button>
          )}
          <Button variant="outline" size="sm" disabled={downloading} onClick={() => void download()}>
            {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            Download CSV
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={update.isPending}
            onClick={() =>
              update.mutate(
                { publicId: campaign.publicId, patch: { archived: !campaign.archivedAt } },
                { onSuccess: () => toast.success(campaign.archivedAt ? 'Campaign restored' : 'Campaign archived') },
              )
            }
          >
            {campaign.archivedAt ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
            {campaign.archivedAt ? 'Restore' : 'Archive'}
          </Button>
        </div>
      </header>

      {campaign.lastSyncError && (
        <Note tone="warning" role="status">
          The last results update failed: {campaign.lastSyncError}
        </Note>
      )}
      {problems.map((p) => (
        <Note
          key={p}
          tone="warning"
          role="status"
          action={
            instantly && retryable > 0 && /pushed/.test(p) ? (
              <Button
                size="sm"
                variant="outline"
                disabled={retry.isPending}
                onClick={() =>
                  retry.mutate(campaign.publicId, {
                    onSuccess: (r) =>
                      r.failed > 0
                        ? toast.error(`${r.failed.toLocaleString()} still failed`, { description: r.error ?? undefined })
                        : toast.success(`Added ${r.added.toLocaleString()} to the campaign`),
                    onError: (e) => toast.error((e as Error).message),
                  })
                }
              >
                <RotateCw className={cn('h-3.5 w-3.5', retry.isPending && 'animate-spin')} />
                Retry
              </Button>
            ) : undefined
          }
        >
          {p}.
        </Note>
      ))}

      <FunnelStrip funnel={funnel} provider={campaign.provider} onPick={(stage) => set({ stage })} active={url.stage} />
      {instantly && (funnel.bounced > 0 || funnel.unsubscribed > 0 || funnel.skipped > 0) && (
        <p className="text-sm text-muted-foreground">
          {[
            funnel.bounced > 0 && `${funnel.bounced.toLocaleString()} bounced`,
            funnel.unsubscribed > 0 && `${funnel.unsubscribed.toLocaleString()} unsubscribed`,
            funnel.skipped > 0 && `${funnel.skipped.toLocaleString()} not accepted by Instantly`,
          ]
            .filter(Boolean)
            .join(' · ')}
          . Bounced and unsubscribed addresses are never emailed again.
        </p>
      )}

      {instantly && progress && <SequenceStrip progress={progress} stepCount={stepCount} onPick={(stage) => set({ stage })} active={url.stage} />}

      <BreakdownTable breakdowns={breakdowns} provider={campaign.provider} />

      <section className="space-y-3">
        <h2 className="text-base font-semibold">Recipients</h2>
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Search" className="min-w-[12rem] flex-1">
            <DebouncedInput placeholder="@username or email" value={url.q} onCommit={(q) => set({ q })} />
          </Field>
          <Field label="Status">
            <Combobox className="w-full md:w-44" value={url.stage} onChange={(stage) => set({ stage })} allowAll allLabel="Everyone" options={stageOptions} />
          </Field>
          <Field label="Email type">
            <Combobox
              className="w-full md:w-40"
              value={url.kind}
              onChange={(kind) => set({ kind })}
              allowAll
              allLabel="Any type"
              options={EMAIL_KINDS.map((k) => ({ value: k, label: KIND_LABELS[k] }))}
            />
          </Field>
        </div>
        {recipients.error && (
          <Note tone="error" role="alert">
            Couldn't load recipients: {(recipients.error as Error).message}
          </Note>
        )}
        <DataTable>
          <DataTableHeader>
            <tr>
              <DataTableHeaderCell>Creator</DataTableHeaderCell>
              <DataTableHeaderCell>Email</DataTableHeaderCell>
              <DataTableHeaderCell>Segment</DataTableHeaderCell>
              <DataTableHeaderCell>Status</DataTableHeaderCell>
              {instantly && <DataTableHeaderCell>Sequence</DataTableHeaderCell>}
              {instantly && <DataTableHeaderCell numeric>Opens</DataTableHeaderCell>}
              {instantly && <DataTableHeaderCell numeric>Clicks</DataTableHeaderCell>}
              <DataTableHeaderCell>Last activity</DataTableHeaderCell>
            </tr>
          </DataTableHeader>
          <tbody>
            {(recipients.data?.recipients ?? []).length === 0 && (
              <DataTableRow>
                <DataTableCell colSpan={instantly ? 8 : 5} muted className="py-8 text-center">
                  {recipients.isLoading ? 'Loading…' : 'Nobody matches these filters.'}
                </DataTableCell>
              </DataTableRow>
            )}
            {(recipients.data?.recipients ?? []).map((r) => (
              <RecipientRow key={r.id} r={r} instantly={instantly} stepCount={stepCount} />
            ))}
          </tbody>
        </DataTable>
        <Pagination page={Math.max(1, url.page)} limit={PAGE_SIZE} total={recipients.data?.total ?? 0} onPageChange={(page) => setUrl({ page })} />
      </section>
    </div>
  );
}

function RecipientRow({ r, instantly, stepCount }: { r: Recipient; instantly: boolean; stepCount: number | null }): JSX.Element {
  const last = r.lastEventAt ?? r.postedAt ?? r.joinedAt ?? r.pushedAt;
  return (
    <DataTableRow>
      <DataTableCell anchor data-label="Creator">
        <a href={profileUrl(r.platform, r.username)} target="_blank" rel="noreferrer" className="hover:underline">
          @{r.username}
        </a>
        <span className="ml-2 text-sm font-normal text-muted-foreground">
          {PLATFORM_LABELS[r.platform]}
          {r.segment?.followers != null && ` · ${compact(r.segment.followers)}`}
        </span>
      </DataTableCell>
      <DataTableCell data-label="Email">{r.email}</DataTableCell>
      <DataTableCell data-label="Segment" muted>
        {KIND_LABELS[r.emailKind] ?? r.emailKind}
        {r.segment?.face && r.segment.face !== 'unknown' && ` · ${FACE_LABELS[r.segment.face].toLowerCase()}`}
      </DataTableCell>
      <DataTableCell data-label="Status">
        <SendStatusPill status={r.status} joined={!!r.joinedAt} posted={!!r.postedAt} />
        {r.interest && <span className="ml-2 text-sm text-muted-foreground">{r.interest.replace(/_/g, ' ')}</span>}
        {r.detail && (r.status === 'skipped' || r.status === 'failed') && (
          <div className="mt-0.5 max-w-[28rem] truncate text-sm text-muted-foreground" title={r.detail}>
            {r.detail}
          </div>
        )}
      </DataTableCell>
      {instantly && (
        <DataTableCell data-label="Sequence">
          <div className="whitespace-nowrap">{recipientStep(r, stepCount)}</div>
          {(r.lastStepAt || r.lastSender) && (
            <div className="text-sm text-muted-foreground" title="The mailbox that sent the latest email. Reply from this one.">
              {[r.lastStepAt && when(r.lastStepAt), r.lastSender].filter(Boolean).join(' · ')}
            </div>
          )}
        </DataTableCell>
      )}
      {instantly && <DataTableCell numeric data-label="Opens">{r.openCount || '—'}</DataTableCell>}
      {instantly && <DataTableCell numeric data-label="Clicks">{r.clickCount || '—'}</DataTableCell>}
      <DataTableCell data-label="Last activity" muted>{when(last)}</DataTableCell>
    </DataTableRow>
  );
}
